import { useRef, useState } from "react";
import { BookOpen, ChevronDown, Download, FileText } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const replaceScenarioIpPlaceholders = (value) => {
  if (typeof value !== "string") return value;
  return value.replace(/\b(?:localhost|127\.0\.0\.1)\b/g, "SCENARIO_IP");
};

const markdownComponents = {
  h1: ({ children }) => (
    <h1 className="text-2xl font-bold text-(--color-text-primary) mt-0 mb-4 pb-2 border-b border-(--color-border)">
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-lg font-bold text-(--color-text-primary) mt-6 mb-3 pb-2 border-b border-(--color-border)">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-base font-bold text-(--color-text-primary) mt-4 mb-2">
      {children}
    </h3>
  ),
  h4: ({ children }) => (
    <h4 className="text-sm font-bold text-(--color-text-primary) mt-3 mb-1">
      {children}
    </h4>
  ),
  p: ({ children }) => (
    <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed my-2">
      {children}
    </p>
  ),
  strong: ({ children }) => (
    <strong className="font-semibold text-(--color-text-primary)">
      {children}
    </strong>
  ),
  em: ({ children }) => (
    <em className="italic text-gray-600 dark:text-gray-400">{children}</em>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-(--color-primary)/50 bg-(--color-primary)/5 px-4 py-2 my-3 rounded-r-lg text-gray-600 dark:text-gray-400 text-sm">
      {children}
    </blockquote>
  ),
  // FIX: react-markdown v8+ no longer passes an `inline` prop to the
  // code renderer. Instead, fenced/block code blocks are always given
  // a `className` like "language-xxx", while truly inline backtick
  // code has no className at all. Use that to distinguish the two.
  code: ({ className, children, ...props }) => {
    const isBlock = Boolean(className);
    return isBlock ? (
      <code className="block" {...props}>
        {children}
      </code>
    ) : (
      <code
        className="text-(--color-primary) bg-(--color-primary)/10 px-1.5 py-0.5 rounded text-xs font-mono"
        {...props}
      >
        {children}
      </code>
    );
  },
  pre: ({ children }) => (
    <pre className="bg-gray-900 dark:bg-black border border-(--color-border) rounded-md p-4 overflow-x-auto text-gray-100 text-xs leading-relaxed my-3">
      {children}
    </pre>
  ),
  ul: ({ children }) => (
    <ul className="list-disc list-outside space-y-1 my-2 text-sm text-gray-700 dark:text-gray-300 pl-5">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal list-outside space-y-1 my-2 text-sm text-gray-700 dark:text-gray-300 pl-5">
      {children}
    </ol>
  ),
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  hr: () => <hr className="border-(--color-border) my-6" />,
  table: ({ children }) => (
    <div className="overflow-x-auto my-4">
      <table className="w-full text-sm border-collapse">{children}</table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-(--color-primary)/10">{children}</thead>
  ),
  th: ({ children }) => (
    <th className="border border-(--color-border) px-3 py-2 text-left font-semibold text-(--color-text-primary)">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border border-(--color-border) px-3 py-2 text-gray-700 dark:text-gray-300">
      {children}
    </td>
  ),
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-(--color-primary) underline underline-offset-2 hover:opacity-80"
    >
      {children}
    </a>
  ),
};

const createPrintWindow = ({ title, sectionTitle, contentHtml }) => {
  const safeTitle = title || "Remediation";
  const printWindow = window.open("", "_blank");
  if (!printWindow) return;

  printWindow.document.write(`
    <!DOCTYPE html>
    <html lang="en">
      <head>
        <meta charset="UTF-8" />
        <title>${safeTitle} - ${sectionTitle}</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 13px; line-height: 1.6; color: #1a1a1a; padding: 40px 48px; max-width: 860px; margin: 0 auto; }
          h1 { font-size: 22px; font-weight: 700; margin-bottom: 14px; padding-bottom: 8px; border-bottom: 2px solid #e5e7eb; color: #111; }
          h2 { font-size: 17px; font-weight: 700; margin-top: 28px; margin-bottom: 10px; padding-bottom: 6px; border-bottom: 1px solid #e5e7eb; color: #111; }
          h3 { font-size: 14px; font-weight: 700; margin-top: 18px; margin-bottom: 6px; color: #111; }
          h4 { font-size: 13px; font-weight: 700; margin-top: 12px; margin-bottom: 4px; color: #111; }
          p { margin: 6px 0; color: #374151; }
          strong { font-weight: 600; color: #111; }
          em { color: #4b5563; font-style: italic; }
          blockquote { border-left: 4px solid #16a34a; background: #f0fdf4; padding: 10px 14px; margin: 12px 0; border-radius: 0 6px 6px 0; color: #374151; }
          blockquote p { margin: 3px 0; }
          code { background: #f0fdf4; color: #15803d; padding: 1px 5px; border-radius: 4px; font-size: 11px; font-family: 'Courier New', monospace; }
          pre { background: #1a1a1a; color: #f3f4f6; padding: 14px 16px; border-radius: 8px; overflow-x: auto; margin: 10px 0; border: 1px solid #374151; }
          pre code { background: none; color: inherit; padding: 0; font-size: 11px; }
          ul { list-style: disc; padding-left: 22px; margin: 6px 0; color: #374151; }
          ol { list-style: decimal; padding-left: 22px; margin: 6px 0; color: #374151; }
          li { margin: 2px 0; }
          hr { border: none; border-top: 1px solid #e5e7eb; margin: 20px 0; }
          table { width: 100%; border-collapse: collapse; margin: 12px 0; font-size: 12px; }
          thead { background: #f0fdf4; }
          th { border: 1px solid #d1fae5; padding: 8px 10px; text-align: left; font-weight: 600; color: #111; }
          td { border: 1px solid #e5e7eb; padding: 7px 10px; color: #374151; }
          tr:nth-child(even) td { background: #f9fafb; }
          a { color: #15803d; text-decoration: underline; }
          @media print {
            body { padding: 20px; }
            pre { white-space: pre-wrap; word-break: break-all; }
          }
        </style>
      </head>
      <body>
        <h1 style="border-bottom:2px solid #16a34a">${safeTitle} - ${sectionTitle}</h1>
        ${contentHtml}
      </body>
    </html>
  `);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
  }, 500);
};

export default function LabRemediationTab({
  isLoadingScenario,
  isLocked,
  playbookMarkdown,
  runbookMarkdown,
  title,
}) {
  const playbookRef = useRef(null);
  const runbookRef = useRef(null);
  const [isPlaybookOpen, setIsPlaybookOpen] = useState(false);
  const [isRunbookOpen, setIsRunbookOpen] = useState(false);

  const hasPlaybook = Boolean(playbookMarkdown && playbookMarkdown.trim());
  const hasRunbook = Boolean(runbookMarkdown && runbookMarkdown.trim());

  const sanitizedPlaybook = replaceScenarioIpPlaceholders(playbookMarkdown);
  const sanitizedRunbook = replaceScenarioIpPlaceholders(runbookMarkdown);

  const handleDownload = (sectionTitle, sectionRef) => {
    if (!sectionRef?.current) return;
    const contentHtml = sectionRef.current.innerHTML;
    if (!contentHtml) return;
    createPrintWindow({
      title,
      sectionTitle,
      contentHtml,
    });
  };

  if (isLoadingScenario) {
    return (
      <div className="space-y-4 p-2">
        <div className="h-7 bg-linear-to-r from-gray-200 via-gray-300 to-gray-200 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 rounded-md animate-pulse bg-size-[200%_100%] w-48"></div>
        <div className="space-y-4">
          {[0, 1].map((idx) => (
            <div
              key={idx}
              className="rounded-md border border-(--color-border) p-4 space-y-3"
            >
              <div className="h-4 bg-linear-to-r from-gray-200 via-gray-300 to-gray-200 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 rounded-md animate-pulse bg-size-[200%_100%] w-32"></div>
              <div className="h-4 bg-linear-to-r from-gray-200 via-gray-300 to-gray-200 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 rounded-md animate-pulse bg-size-[200%_100%] w-full"></div>
              <div className="h-4 bg-linear-to-r from-gray-200 via-gray-300 to-gray-200 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 rounded-md animate-pulse bg-size-[200%_100%] w-5/6"></div>
              <div className="h-24 bg-linear-to-r from-gray-200 via-gray-300 to-gray-200 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 rounded-md animate-pulse bg-size-[200%_100%] w-full"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!hasPlaybook && !hasRunbook) {
    return (
      <div className="text-center py-12 text-(--color-text-muted) dark:text-gray-400">
        <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p className="text-base font-semibold text-(--color-text-primary) mb-1">
          No Resources Available
        </p>
        <p className="text-sm">
          No Response and Remediation resources are available for this Box.
        </p>
      </div>
    );
  }

  if (isLocked) {
    return (
      <div className="text-center py-12 text-(--color-text-muted) dark:text-gray-400">
        <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p className="text-base font-semibold text-(--color-text-primary) mb-1">
          Resources Locked
        </p>
        <p className="text-sm">
          Response and remediation unlock after each question is attempted at
          least once.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6 pb-5 border-b border-(--color-border)">
        <div>
          <h2 className="text-lg font-bold text-(--color-text-primary)">
            Response and Remediation Resources
          </h2>
          <p className="text-sm text-(--color-text-muted) dark:text-gray-400 mt-1">
            Review the playbook and runbook guidance for this Box.
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="rounded-md border border-(--color-border) bg-transparent dark:bg-black/20">
          <button
            type="button"
            onClick={() => setIsPlaybookOpen((prev) => !prev)}
            className="w-full flex items-center justify-between gap-4 px-4 py-3 border-b border-(--color-border) text-(--color-text-primary)"
            aria-expanded={isPlaybookOpen}
            aria-controls="remediation-playbook"
          >
            <span className="flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              <span className="text-sm font-semibold">Playbook</span>
            </span>
            <ChevronDown
              className={`h-4 w-4 text-(--color-text-muted) transition-transform ${
                isPlaybookOpen ? "rotate-180" : ""
              }`}
            />
          </button>
          {isPlaybookOpen && (
            <div id="remediation-playbook" className="px-4 py-4">
              {hasPlaybook ? (
                <div className="space-y-4">
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => handleDownload("Playbook", playbookRef)}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-(--color-primary) text-(--color-primary) font-semibold text-xs hover:bg-(--color-primary)/10 transition-all"
                    >
                      <Download className="h-3.5 w-3.5" />
                      Download PDF
                    </button>
                  </div>
                  <div ref={playbookRef} className="max-w-none space-y-4">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={markdownComponents}
                    >
                      {sanitizedPlaybook}
                    </ReactMarkdown>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-(--color-text-muted) dark:text-gray-400">
                  Playbook is not available for this Box.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="rounded-md border border-(--color-border) bg-transparent dark:bg-black/20">
          <button
            type="button"
            onClick={() => setIsRunbookOpen((prev) => !prev)}
            className="w-full flex items-center justify-between gap-4 px-4 py-3 border-b border-(--color-border) text-(--color-text-primary)"
            aria-expanded={isRunbookOpen}
            aria-controls="remediation-runbook"
          >
            <span className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              <span className="text-sm font-semibold">Runbook</span>
            </span>
            <ChevronDown
              className={`h-4 w-4 text-(--color-text-muted) transition-transform ${
                isRunbookOpen ? "rotate-180" : ""
              }`}
            />
          </button>
          {isRunbookOpen && (
            <div id="remediation-runbook" className="px-4 py-4">
              {hasRunbook ? (
                <div className="space-y-4">
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => handleDownload("Runbook", runbookRef)}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-(--color-primary) text-(--color-primary) font-semibold text-xs hover:bg-(--color-primary)/10 transition-all"
                    >
                      <Download className="h-3.5 w-3.5" />
                      Download PDF
                    </button>
                  </div>
                  <div ref={runbookRef} className="max-w-none space-y-4">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={markdownComponents}
                    >
                      {sanitizedRunbook}
                    </ReactMarkdown>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-(--color-text-muted) dark:text-gray-400">
                  Runbook is not available for this Box.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
